// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect, useMemo } from 'react';
import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import useFetch from 'Hooks/useFetch';
import useAsyncError from 'Hooks/useAsyncError';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import download from 'Utils/download';
import useAnalytics from 'Hooks/useAnalytics';

import OrdersComponent from 'Components/Orders/Orders';
import { API_ORDERS, API_ORDERS_FEED, API_ORDER_INVOICE } from 'Constants';

const defaultSortId = 'added-desc';

const Orders = memo(() => {
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [deleteReservationIdLoading, setDeleteReservationIdLoading] = useState(null);
  const [ordersById, setOrdersById] = useState({});
  const [errors, setErrors] = useState([]);

  const throwError = useAsyncError();
  const fetcher = useFetch();
  const { t } = useTranslation();
  const { location, query, push } = useRouter();
  const { data: { email, loggedIn } } = useUser();
  const analytics = useAnalytics();

  const sortOptions = useMemo(() => [
    { id: 'order_number-asc', label: `${t('pages.id')} ${t('orders.ascending')}` },
    { id: 'order_number-desc', label: `${t('pages.id')} ${t('orders.descending')}` },
    { id: 'invoice_number-asc', label: `${t('pages.invoiceNumber')} ${t('orders.ascending')}` },
    { id: 'invoice_number-desc', label: `${t('pages.invoiceNumber')} ${t('orders.descending')}` },
    { id: 'added-asc', label: `${t('pages.added')} ${t('orders.ascending')}` },
    { id: 'added-desc', label: `${t('pages.added')} ${t('orders.descending')}` },
    { id: 'user-asc', label: `${t('pages.user')} ${t('orders.ascending')}` },
    { id: 'user-desc', label: `${t('pages.user')} ${t('orders.descending')}` },
    { id: 'internal_status-asc', label: `${t('pages.orderStatus')} ${t('orders.ascending')}` },
    { id: 'internal_status-desc', label: `${t('pages.orderStatus')} ${t('orders.descending')}` },
    { id: 'order_type-asc', label: `${t('pages.orderType')} ${t('orders.ascending')}` },
    { id: 'order_type-desc', label: `${t('pages.orderType')} ${t('orders.descending')}` },
    { id: 'delivery_stock-asc', label: `${t('pages.deliveryStock')} ${t('orders.ascending')}` },
    { id: 'delivery_stock-desc', label: `${t('pages.deliveryStock')} ${t('orders.descending')}` },
    { id: 'total_value-asc', label: `${t('pages.totalValue')} ${t('orders.ascending')}` },
    { id: 'total_value-desc', label: `${t('pages.totalValue')} ${t('orders.descending')}` },
  ], []);

  const defaultSort = sortOptions[5];
  const currentSortId = query?.order_by
    ? `${ query.order_by }-${ query.order_direction }`
    : defaultSortId;
  const [currentSort, setSort] = useState(
    currentSortId !== defaultSort.id
      ? sortOptions.find(item => item.id === currentSortId)
      : defaultSort
  );

  useEffect(() => {
    // check if query has sort
    if (Object.keys(omit(query || {}, [
      'internal_status_id',
      'user',
      'order_type',
      'delivery_stock',
      'start_date',
      'end_date',
      'product',
      'drop_shipment',
      'reference',
      'page'
    ])).length) {
      const { order_by: orderBy, order_direction: orderDirection } = query;
      setSort(sortOptions.find(item => item.id === `${ orderBy }-${ orderDirection }`));
    }
  }, []);

  const onMobileOrderChange = sort => {
    const [orderBy, orderDirection] = sort.split('-');
    analytics.track('sort_orders', { order_by: orderBy, order_direction: orderDirection });

    if (orderDirection === 'none' || sort === defaultSortId) {
      setSort(sortOptions.find(item => item.id === defaultSortId));
      push({
        location: location.pathname,
        search: stringify(omit(query || {}, ['order_by', 'order_direction']))
      });
      return;
    }

    setSort(sortOptions.find(item => item.id === sort));

    const queryParams = {
      ...(omit(query || {}, ['order_by', 'order_direction', 'page'])),
      order_by: orderBy,
      order_direction: orderDirection
    };

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  const getOrders = () => {
    setOrdersLoading(true);

    let path = API_ORDERS;

    if (query) {
      path += `?${ stringify({ ...query }) }`;
    }

    fetcher(path).then(res => {
      if (res.length === 0) {
        setOrders([]);
        setPagination(null);
        return;
      }

      const resPagination = {
        currentPage: res?.current_page,
        recordsPerPage: res?.records_perPage,
        totalPages: res?.total_pages,
        totalRecords: res?.total_records
      };

      const mappedOrders = res.orders.map(order => ({
        ...order,
        dropShipment: order?.drop_shipment,
        orderNumber: {
          id: order?.order_number,
          dropShipment: order?.drop_shipment,
          statusId: order?.status_id,
          invoiceId: order?.invoices?.[0]?.id,
        },
        invoiceNumber: order?.invoice_number,
        userFullName: `${order?.user_first_name || ''} ${order?.user_last_name || ''}`,
        status: {
          status: order?.status,
          statusName: order?.status_name,
          statusId: order?.status_id,
          expirationDate: order?.expiration_date,
        },
        orderType: order?.order_type,
        deliveryStock: order?.delivery_stock,
        totalValue: {
          isCashOnDelivery: order?.is_cash_on_delivery,
          totalValue: order?.total_value,
          totalCashOnDelivery: order?.total_cash_on_delivery,
          totalWitchVat: order?.total_value_with_vat,
          dropShipment: order?.drop_shipment,
          dropShipmentCurrency: order?.drop_shipment_currency?.currency_id,
        },
        actions: {
          orderId: order?.id,
          statusId: order?.status_id,
          invoiceId: order?.invoices?.[0]?.id,
          id: order?.order_number,
        }
      }));

      setOrders(mappedOrders);
      setPagination(resPagination);
    }).catch(err => throwError(err))
      .finally(() => setOrdersLoading(false));
  };

  // Get Orders on page load
  useEffect(() => {
    if (loggedIn) {
      getOrders();
    }

    // if (Object.keys(query || {}).length === 0 && !loggedIn) {
    //   push({
    //     location: location.pathname,
    //     search: ''
    //   });
    // }
  }, [location.pathname, query, email, loggedIn]);

  const handleDeleteReservation = orderId => {
    analytics.track('order_deleted', { order_id: orderId });
    setDeleteReservationIdLoading(orderId);
    fetcher(`${ API_ORDERS }/${ orderId }`, { method: 'DELETE' })
      .then(() => {
        getOrders();
        setErrors([{ message: t('orders.orderDeleted'), type: 'success' }]);
      })
      .catch(() => {
        setErrors([{ message: t('orders.orderDeletedError'), type: 'error' }]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .finally(() => {
        setDeleteReservationIdLoading('');
      });
  };

  const handleDownload = (downloadType, isPDF, invoiceId) => {
    let path;

    if (downloadType === 'invoice') {
      analytics.track('file_download', { type: isPDF ? 'invoice' : 'warranty' });
      path = `${ API_ORDER_INVOICE }/${invoiceId}/${ isPDF ? 'export?format=pdf' : 'warranty' }`;
    }

    if (downloadType === 'feed') {
      analytics.track('file_download', { type: isPDF ? 'invoice_all' : 'warranty_all' });
      path = `${ API_ORDERS_FEED }?format=${ isPDF ? 'pdf' : 'csv' }`;
    }

    const queryParams = omit((query || {}), ['page']);

    if (Object.keys(queryParams).length > 0) {
      path += `&${ stringify({ ...queryParams }) }`;
    }

    download(path);
  };

  const handleOrderById = async id => {
    analytics.track('order_expand');

    if (id in ordersById) return;
    fetcher(`${ API_ORDERS }/${id}`).then(res => {
      setOrdersById(obj => ({ ...obj, [id]: res?.items }));
    }).catch(err => throwError(err))
      .finally(() => setOrdersLoading(false));
  };

  return (
    <OrdersComponent
      orders={ orders }
      ordersLoading={ ordersLoading }
      pagination={ pagination }
      onDeleteReservation={ handleDeleteReservation }
      deleteReservationIdLoading={ deleteReservationIdLoading }
      onDownload={ handleDownload }
      ordersById={ ordersById }
      onGetOrderById={ handleOrderById }
      sortOptions={ sortOptions }
      defaultSortId={ defaultSortId }
      currentSort={ currentSort }
      handleMobileOrderChange={ onMobileOrderChange }
      errors={ errors }
    />
  );
});

Orders.displayName = 'OrdersContainer';

export default Orders;
